<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <svg class="logo-svg" width="68" height="68" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="17" stroke="currentColor" stroke-width="2"/>
          <circle cx="18" cy="18" r="10" stroke="currentColor" stroke-width="2"/>
          <circle cx="18" cy="18" r="3" fill="currentColor"/>
        </svg>
        <h2>Discogs Collection</h2>
        <p>Connect to view and browse your vinyl collection</p>
      </div>

      <div v-if="isLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Connecting to Discogs...</p>
      </div>

      <div v-else>
        <div v-if="error" class="error-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {{ error }}
        </div>

        <div class="login-buttons">
          <button
            class="login-button discogs-button"
            @click="onConnectClick"
            :disabled="isLoading">
            Connect with Discogs
          </button>
        </div>

        <div class="features">
          <div class="feature-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>Browse your entire collection</span>
          </div>
          <div class="feature-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <span>Search by artist, album, or label</span>
          </div>
          <div class="feature-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18V5l12-2v13"></path>
              <circle cx="6" cy="18" r="3"></circle>
              <circle cx="18" cy="16" r="3"></circle>
            </svg>
            <span>Discover new music from Discogs</span>
          </div>
        </div>
      </div>

      <div class="login-footer">
        <p>By connecting, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { initiateAuth } from '@/services/auth';
import { savePostLoginRedirect } from '@/services/postLoginRedirect';

export default defineComponent({
  name: 'LoginForm',

  setup() {
    const route = useRoute();
    console.log('LoginForm: setup called');
    const isLoading = ref(false);
    const error = ref(null);

    onMounted(() => {
      console.log('LoginForm: component mounted');
    });

    // Connect with Discogs
    const onConnectClick = async () => {
      console.log('Connect button clicked!');
      isLoading.value = true;
      error.value = null;

      try {
        // Return to whichever host the app is served from (localhost, web.app or custom domain)
        const callbackUrl = `${window.location.origin}/callback`;
        console.log('Callback URL:', callbackUrl);

        // Start Discogs OAuth flow directly (without Firebase)
        savePostLoginRedirect(route.query.redirect);
        const authUrl = await initiateAuth(callbackUrl);

        if (!authUrl) {
          throw new Error('Failed to get authorization URL from Discogs');
        }

        // Redirect to Discogs for authentication
        console.log('Redirecting to Discogs:', authUrl);
        window.location.href = authUrl;
      } catch (err) {
        console.error('Login error:', err);
        error.value = err.message || 'An error occurred during login.';
        isLoading.value = false;
      }
    };

    return {
      isLoading,
      error,
      onConnectClick,
    };
  },
});
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 64px);
  padding: 2rem 1rem;
  background: linear-gradient(0deg, var(--color-background) 0%, rgba(30, 30, 30, 0.8) 100%);
}

.login-card {
  background-color: rgba(24, 24, 24, 0.9);
  border-radius: var(--border-radius);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  padding: 2.5rem;
  width: 100%;
  max-width: 420px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-svg {
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 0 8px rgba(29, 185, 84, 0.4));
}

.login-header h2 {
  margin-bottom: 0.75rem;
  color: var(--color-text);
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.login-header p {
  color: var(--color-text-secondary);
  margin-top: 0;
  font-size: 1.05rem;
}

.login-buttons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-pill);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  color: white;
  background-color: var(--color-primary);
  font-size: 1rem;
  letter-spacing: 0.02em;
}

.discogs-button {
  background-color: var(--color-primary);
}

.discogs-button:hover {
  background-color: var(--color-primary-light);
  transform: scale(1.02);
  box-shadow: 0 0 10px rgba(29, 185, 84, 0.4);
}

.button-icon {
  margin-right: 0.75rem;
  width: 24px;
  height: 24px;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.75rem;
  padding: 1.75rem 0.5rem;
  border-top: 1px solid var(--color-divider);
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--color-text-secondary);
  font-size: 1rem;
}

.feature-item svg {
  color: var(--color-primary);
  flex-shrink: 0;
}

.error-message {
  background-color: rgba(232, 70, 70, 0.1);
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #e84646;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.9rem;
  border: 1px solid rgba(232, 70, 70, 0.2);
}

.error-message svg {
  color: #e84646;
  flex-shrink: 0;
}

.login-footer {
  text-align: center;
  margin-top: 2.5rem;
  font-size: 0.8rem;
  color: var(--color-text-disabled);
  line-height: 1.5;
}

.login-footer a {
  color: var(--color-primary);
  text-decoration: none;
}

.login-footer a:hover {
  text-decoration: underline;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 0;
}

.loading-container p {
  color: var(--color-text-secondary);
  margin-top: 1.5rem;
  font-size: 1.05rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 3px solid var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .login-card {
    padding: 2rem 1.5rem;
  }

  .login-header h2 {
    font-size: 1.5rem;
  }

  .features {
    padding: 1.5rem 0;
  }
}
</style>
